import React, { useEffect, useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { FormControl, Grid, TextField } from '@material-ui/core';
import { DataGrid  } from '@mui/x-data-grid';

import { BannerType, ComponentController, Dropdown, FormButton } from 'components/common';
import { GeneralDetailsFields } from 'components/SubmissionWizard/Helpers';

export const DefaultSurplusLineName = "surplusLine"

export const SurplusLine = ({
  name = DefaultSurplusLineName,
  dropdownsData,
  fields,
  control,
  trigger,
  errors,
  setFormState,
  setBannerState,
  validationRef,
  isRequired = false,
  inputRef
}
) => {
  const columnsConfig = [
    {
      field: 'code',
      headerName: 'Code',
      minWidth: 250,
      datasource: name,
      type: "singleSelect",
      editable: true,
      sortable: false,
      resizable: false,
      disableColumnMenu: true,
      dropdownContainer: true,
      renderEditCell: {},
      forceRerender: false,
      mandatory: true
    },
    {
      field: 'description',
      headerName: 'Description',
      minWidth: 250,
      sortable: false,
      resizable: false,
      disableColumnMenu: true,
      dropdownContainer: true,
      renderEditCell: {},
      mandatory: true
    }];
  const [selectedCell, setSelectedCell] = useState();
  const [columns, setColumns] = useState(columnsConfig);
  const fieldsRef = React.useRef();
  const baseValidationRef = validationRef.current;

  fieldsRef.current = fields;

  useEffect(() => {
    let columns = columnsConfig.map(c => {
      if (c.dropdownContainer) {
        c.renderEditCell = dropdownContainer;
      }
      if (c.mandatory) {
        c.headerName = `${c.headerName}*`;
      }
      return c;
    });

    setColumns(columns);
  }, []);

  let validateGrid = () => {
    let surplusLineValidationStatus = fieldsRef.current.surplusLineGrid.map(x => {
      var hasEmpty = columnsConfig
        .filter(c => c.mandatory)
        .some(c => x[c.field] === '');
      return !hasEmpty;
    });
    return surplusLineValidationStatus.every(x => x);
  }

  useEffect(() => {
    validationRef.current = async () => {
      const isBaseFormValid = await baseValidationRef();
      if (!isBaseFormValid) return false;
      
      let isGridVisible = fieldsRef.current.surplusLineGrid.length > 0;

      if (!isRequired && !isGridVisible) {
        return true;
      }

      let isValid = validateGrid();

      if (!isValid) {
        let message = isGridVisible ? "Please fill the surplus line data" : "Surplus line is required field";

        setBannerState({
          show: true,
          type: BannerType.warning,
          message: message
        });
      }

      return isValid;
    };
  }, [setBannerState, trigger, validationRef, fieldsRef.current, fieldsRef.current.surplusLineGrid])

  const getDropdownContainerData = useCallback(params => {
    let source = params.colDef.field;
    let id = params.id;
    let data = {};
    let value = {};
    let properties = dropdownsData.surplusLines?.find(z => z.surplusLineType === fieldsRef.current.surplusLine)?.properties;
    let codes = properties?.map(property => property.code) ?? [];
    let currentValue = fieldsRef.current.surplusLineGrid.find(x => x.id === id);
    if (source === 'code') {
      data = codes;
      value = currentValue.code ?? ""
    }

    return { source, data, id, value };
  }, [dropdownsData.surplusLines]);

  const dropdownContainer = params => {
    let data = getDropdownContainerData(params);
    const { id, field, api } = params;

    return data && (
      <ComponentController
        name={data.source}
        control={control}
        required
        render={({ field: { name, onBlur, onChange } }) =>
          <FormControl fullWidth error={!!errors[name]}>
            <Dropdown
              id={data.id}
              name={name}
              value={data.value}
              data={data.data}
              onChange={e => { handleDropDownChange(e.target.name, e.target.value, data.id); onChange(e); api.setCellMode(id, field, 'view');}}
              onBlur={onBlur}
              hideLabel={true}
            />
          </FormControl>
        }
      />
    );
  }

  const getRequiredError = name => {
    return errors[name]?.type === 'required' &&
      <span role="alert">"{GeneralDetailsFields[name]}" is required</span>;
  }

  const onSurplusLineChange = useCallback(surplusLine => {
    let selectedSurplusLine = dropdownsData.surplusLines?.find(z => z.surplusLineType === surplusLine);
    let properties = selectedSurplusLine?.properties || [];
    let initialSurplusLineGrid = [];

    if (properties.length > 0 && fields.surplusLineGrid.length === 0) {
      initialSurplusLineGrid.push(
        {
          id: uuidv4(),
          code: '',
          description: ''
        });
    }

    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        surplusLine: surplusLine ?? '',
        surplusLineGrid: initialSurplusLineGrid
      }
    }));
  }, [setFormState]);

  const surplusLineCodesCheck = (surplusLine) => {
    let selectedSurplusLine = dropdownsData.surplusLines?.find(z => z.surplusLineType === surplusLine);
    let properties = selectedSurplusLine?.properties || [];
    if (properties.length > 0) {
      return true;
    }
    return false;
  }

  const onCellClick = useCallback(cell => {
    setSelectedCell(cell);
  }, [setSelectedCell]);

  const removeSurplusLine = useCallback(() => {
    let currentSurplusLineGrid = fieldsRef.current.surplusLineGrid;
    if (currentSurplusLineGrid.length > 1) {
      let surplusLineGrid = currentSurplusLineGrid.filter(x => x.id != selectedCell.id);
      setFormState(previous => ({
        ...previous,
        fields: { ...previous.fields, surplusLineGrid }
      }));
    }
  }, [fieldsRef.current.surplusLineGrid.length, setFormState, selectedCell]);

  const addSurplusLine = () => {
    let initialSurplusLineGrid = [];
    fieldsRef.current.surplusLineGrid.map(x => initialSurplusLineGrid.push(x));

    initialSurplusLineGrid.push(
      {
        id: uuidv4(),
        code: '',
        description: ''
      });

    setFormState(previous => {
      return {
        ...previous,
        fields: {
          ...previous.fields,
          surplusLineGrid: initialSurplusLineGrid
        }
      };
    });
  }

  const handleDropDownChange = useCallback((field, value, id) => {
    setFormState(previous => ({
      ...previous,
      fields: {
        ...previous.fields,
        surplusLineGrid: fieldsRef.current.surplusLineGrid.map(p => {
          if (p.id === id) {
            const isDuplicate = previous.fields.surplusLineGrid.some(row => row.code === value && row.id !== id);
            if (isDuplicate) {
              setBannerState({
                show: true,
                type: BannerType.warning,
                message: "Duplicate codes are not allowed."
              });
              return p;
            }

            p[field] = value;
            let properties = dropdownsData.surplusLines?.find(z => z.surplusLineType === fieldsRef.current.surplusLine)?.properties;
            const matchedDescription = properties?.map(property => property.code === value ? property.description : null)?.find(desc => desc !== null) || '';
            p['description'] = matchedDescription;
          }
          return p;
        })
      }
    }));

    setColumns(previous => {
      return previous.map(p => {
        if ('forceRerender' in p) {
          p.forceRerender = !p.forceRerender;
        }
        return p;
      });
    });
  }, [setFormState, setColumns]);

  const renderSurplusLine = () => {
    return (
      <Grid item>
        <ComponentController
          name={name}
          control={control}
          required={isRequired}
          render={({ field: { name, onBlur, onChange } }) =>
            <FormControl fullWidth error={!!errors[name]}>
              <Dropdown
                id={name}
                name={name}
                emptyValue={true}
                label={GeneralDetailsFields[name]}
                value={fieldsRef.current[name]}
                data={dropdownsData.surplusLines.map(x => x.surplusLineType)}
                onChange={e => { onSurplusLineChange(e.target?.value); onChange(e); }}
                onBlur={onBlur}
                errorText={getRequiredError(name)}
                inputRef={inputRef}
              />
            </FormControl>
          }
        />
      </Grid>
    );
  }

  const renderSurplusLineTable = () => {
    let render = surplusLineCodesCheck(fieldsRef.current.surplusLine);

    return render && (
      <div>
        <DataGrid
          rowHeight={55}
          rows={fieldsRef.current.surplusLineGrid}
          columns={columns}
          density="compact"
          hideFooter={true}
          autoHeight={true}
          onCellClick={e => {onCellClick(e)}}
          onEditRowsModelChange={handleDropDownChange}
        />
        <div style={{ marginTop: '10px' }}>
          <FormButton onClick={addSurplusLine}>Add</FormButton>
          <FormButton onClick={removeSurplusLine}>Remove</FormButton>
        </div>
      </div>
    );
  }

  return <>
    {renderSurplusLine()}
    {renderSurplusLineTable()}    
  </>;
}
